import React, { useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useSocket } from '../contexts/SocketContext';
import { useBranch } from '../contexts/BranchContext';
import SoundService from '../services/soundService';

const getDisplayOrderId = (order) => {
  return order?.orderId || order?._id?.slice(-6).toUpperCase() || 'N/A';
};

const GlobalOrderModal = () => {
  const {
    incomingOrder,
    modalVisible,
    setModalVisible,
    acceptOrder,
    rejectOrder,
    showNextQueuedOrder,
  } = useSocket();
  const { allBranches } = useBranch();
  const processingRef = useRef(false);

  const getBranchName = useCallback((order) => {
    if (!order) return null;
    if (order.branchName) return order.branchName;
    const branchId = order.branchId || order.branch?._id || (typeof order.branch === 'string' ? order.branch : null);
    if (!branchId || !allBranches) return null;
    const branch = allBranches.find(b => b._id === branchId);
    return branch?.name || null;
  }, [allBranches]);

  const handleAccept = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    SoundService.stopOrderAlarm(incomingOrder?._id);
    try {
      const branchId = incomingOrder?.branchId || incomingOrder?.branch?._id;
      await acceptOrder(incomingOrder._id, branchId);
    } finally {
      processingRef.current = false;
      setModalVisible(false);
      showNextQueuedOrder(true);
    }
  };

  const handleReject = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    SoundService.stopOrderAlarm(incomingOrder?._id);
    try {
      const branchId = incomingOrder?.branchId || incomingOrder?.branch?._id;
      await rejectOrder(incomingOrder._id, 'Branch unavailable', branchId);
    } finally {
      processingRef.current = false;
      setModalVisible(false);
      showNextQueuedOrder(true);
    }
  };

  const handleClose = () => {
    if (processingRef.current) return;
    setModalVisible(false);
    showNextQueuedOrder(true);
  };

  if (!incomingOrder) return null;

  const branchName = getBranchName(incomingOrder);
  const customerName = incomingOrder.customerName || incomingOrder.customer?.name || 'Customer';
  const items = incomingOrder.items || [];
  const payoutAmount = incomingOrder.payoutAmount ?? incomingOrder.subTotal ?? incomingOrder.grandTotal ?? 0;

  return (
    <Dialog
      open={modalVisible}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        }
      }}
    >
      <Box sx={{ bgcolor: '#851E41', px: 3, py: 2.5, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#fff', mb: 0.5 }}>
          New Order Received
        </Typography>
        {branchName && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
            <LocationOnIcon sx={{ fontSize: 16, color: '#FDE8EC' }} />
            <Typography sx={{ fontSize: 14, color: '#FDE8EC', fontWeight: 500 }}>
              {branchName}
            </Typography>
          </Box>
        )}
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 0.5 }}>Order #</Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1F2937' }}>
            {getDisplayOrderId(incomingOrder)}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 0.5 }}>Customer</Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1F2937' }}>
            {customerName}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1F2937', mb: 1.5 }}>
          Items
        </Typography>

        <List dense sx={{ bgcolor: '#F9FAFB', borderRadius: 2, p: 0 }}>
          {items.map((item, idx) => (
            <ListItem key={idx} sx={{ px: 2, py: 1 }}>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#1F2937' }}>
                      {item.name || item.foodItem?.name || 'Item'}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                      ₹{((item.price || 0) * (item.quantity || 1)).toLocaleString()}
                    </Typography>
                  </Box>
                }
                secondary={
                  item.quantity > 1 && (
                    <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                      Qty: {item.quantity} × ₹{(item.price || 0).toLocaleString()}
                    </Typography>
                  )
                }
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1F2937' }}>
            Total
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#851E41' }}>
            ₹{payoutAmount.toLocaleString()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleReject}
            disabled={processingRef.current}
            sx={{
              py: 1.5,
              fontSize: 16,
              fontWeight: 700,
              borderColor: '#EF4444',
              color: '#EF4444',
              '&:hover': { borderColor: '#DC2626', bgcolor: '#FEF2F2' },
            }}
          >
            {processingRef.current ? <CircularProgress size={20} sx={{ color: '#EF4444' }} /> : 'Reject'}
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleAccept}
            disabled={processingRef.current}
            sx={{
              py: 1.5,
              fontSize: 16,
              fontWeight: 700,
              bgcolor: '#10B981',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {processingRef.current ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Accept'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalOrderModal;
